/**
 * Multi-location pricing math for OneTapReply.
 *
 * Single source of truth shared by BillingPage and the add-mode Step 3
 * pricing confirmation. Pricing is flat $19.99/mo per location; this helper
 * exists so future pricing changes (per-tier discounts, coupons, currency)
 * land in one place.
 */

import type { OTRSubscription } from '@/types/otr';

export const PER_LOCATION_PRICE_USD = 19.99;
export const ANNUAL_PER_LOCATION_PRICE_USD = 199.90;
export const ANNUAL_SAVINGS_USD = Math.round((PER_LOCATION_PRICE_USD * 12 - ANNUAL_PER_LOCATION_PRICE_USD) * 100) / 100;

export interface MultiLocationTotal {
  /** Number of active (or past_due) paid locations after adding `newCount`. */
  paidLocations: number;
  /** Monthly total in USD across all paid locations. */
  totalUsd: number;
  /** Headline string like "$39.98/mo (2 × $19.99)" or free-plan copy. */
  headline: string;
  /** Optional advisory notes (past-due, coupon, free-tier). */
  notes: string[];
}

/**
 * Compute the post-add monthly total.
 *
 * @param subscriptions current active/past_due subscriptions for the user
 *   (canceled ones are ignored — they don't bill).
 * @param newCount how many locations the user is about to add (typically 1).
 */
export function computeMultiLocationTotal(
  subscriptions: Pick<OTRSubscription, 'status'>[],
  newCount: number,
): MultiLocationTotal {
  const billable = subscriptions.filter(
    (s) => s.status === 'active' || s.status === 'past_due',
  );
  const paidLocations = billable.length + Math.max(0, newCount);
  const notes: string[] = [];

  if (billable.length === 0 && newCount === 0) {
    return {
      paidLocations: 0,
      totalUsd: 0,
      headline: '$0/mo (free plan — subscribe in Billing to enable replies)',
      notes,
    };
  }

  if (billable.length === 0 && newCount > 0) {
    return {
      paidLocations: 0,
      totalUsd: 0,
      headline: '$0/mo (free plan — subscribe in Billing to enable replies)',
      notes,
    };
  }

  const totalUsd = Math.round(paidLocations * PER_LOCATION_PRICE_USD * 100) / 100;
  const formatted = totalUsd.toFixed(2);
  const headline = `$${formatted}/mo (${paidLocations} × $${PER_LOCATION_PRICE_USD.toFixed(2)})`;

  const pastDueCount = billable.filter((s) => s.status === 'past_due').length;
  if (pastDueCount > 0) {
    const which = pastDueCount === 1 ? '1 location has' : `${pastDueCount} locations have`;
    notes.push(`Note: ${which} a past-due payment — resolve in Billing.`);
  }

  return { paidLocations, totalUsd, headline, notes };
}
